/**
 * Native image normalization.
 *
 * Re-encodes a picked image to a plain JPEG at a bounded size before upload.
 * This does three useful things at once:
 *   - Guarantees an `image/jpeg` MIME type the server accepts.
 *   - Strips live/motion-photo video sidecars (only the still frame is decoded).
 *   - Shrinks oversized modern-phone captures under the upload limit.
 *
 * Isolated here so the native modules (expo-image-manipulator, the new
 * expo-file-system `File` API) stay out of the pure validation path.
 */

import { File } from 'expo-file-system';
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator';

/** Longest-edge cap for uploaded photos. */
export const MAX_IMAGE_DIMENSION = 2048;

/** JPEG quality for the re-encode (0-1). */
export const JPEG_COMPRESSION = 0.8;

export interface NormalizedImage {
  uri: string;
  mimeType: 'image/jpeg';
  size: number;
}

export interface SourceDimensions {
  width?: number;
  height?: number;
}

/**
 * Normalize an image to a bounded JPEG.
 *
 * @param uri Source image URI (typically a picker cache `file://` URI).
 * @param source Optional known source dimensions used to decide downscaling.
 */
export async function normalizeToJpeg(
  uri: string,
  source: SourceDimensions = {}
): Promise<NormalizedImage> {
  const context = ImageManipulator.manipulate(uri);

  const width = source.width ?? 0;
  const height = source.height ?? 0;
  if (Math.max(width, height) > MAX_IMAGE_DIMENSION) {
    // Resize the longest edge; the manipulator preserves aspect ratio.
    if (width >= height) {
      context.resize({ width: MAX_IMAGE_DIMENSION });
    } else {
      context.resize({ height: MAX_IMAGE_DIMENSION });
    }
  }

  const image = await context.renderAsync();
  const result = await image.saveAsync({ compress: JPEG_COMPRESSION, format: SaveFormat.JPEG });

  const size = new File(result.uri).size ?? 0;

  return { uri: result.uri, mimeType: 'image/jpeg', size };
}
